import { ReactElement, useEffect } from 'react';
import type { GetServerSideProps, Redirect } from 'next';
import { Box } from '@zoopla-eng/zds-components/box';
import { initializeApollo } from '@zoopla/apollo';
import { GraphQLError } from 'graphql';
import { Head } from '@zoopla/layout/head';
import { Layout } from '@zoopla/house-prices/layout';
import { HousePricesSearch } from '@zoopla/house-prices/search';
import { trackPage } from '@zoopla/tracking';
import SeoSearchLinks from './SeoSearchLinks';
import type {
    GeoSearchQuery,
    GeoSearchQueryVariables
} from './queries/HousePricesLanding.query.generated';
import HOUSE_PRICES_LANDING_QUERY from './queries/HousePricesLanding.query';
import type { CustomPageComponent } from 'components/app/types';
import { useSchema } from '@zoopla/layout/schema';

const breadcrumbs = [
    {
        id: 'Zoopla',
        text: 'Zoopla',
        href: '/'
    },
    {
        id: 'House prices',
        text: 'House prices',
        href: '/house-prices/'
    }
];

const HousePricesLandingPage: CustomPageComponent<Record<string, never>> =
    () => {
        useEffect(() => {
            trackPage('/house-prices/', {
                activity: 'house_prices_search_home',
                section: 'house-prices'
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const schema = useSchema(breadcrumbs, '/house-prices/');

        const head = {
            canonicalPath: '/house-prices/',
            pageTitle: 'Sold House Prices - Get historic data only on Zoopla',
            pageDescription:
                "Get sold house prices and estimates of your property's current value with Zoopla. Browse over 25 million prices for houses and flats across the UK.",
            schema
        };

        return (
            <>
                <Head {...head} />
                <Box
                    fw
                    display="flex"
                    flexFlow="column"
                    alignItems="flex-start"
                >
                    <Box
                        width="100%"
                        pb="$M300"
                        pt={0}
                        bpS200={{ pt: '$S400' }}
                        bpM100={{ pt: 0 }}
                        bpL100={{ pb: '$L200' }}
                    >
                        <HousePricesSearch isLandingPage />
                    </Box>
                    <Box
                        width="100%"
                        px="$viewportS100PageMargin"
                        bpS200={{ px: 0 }}
                    >
                        <SeoSearchLinks linksPerColumn={10} />
                    </Box>
                </Box>
            </>
        );
    };

HousePricesLandingPage.getLayout = (page: ReactElement) => (
    <Layout
        breadcrumbs={breadcrumbs}
        dfpAdTargeting={{ section: 'house-prices' }}
    >
        {page}
    </Layout>
);

const isCrash = (e: GraphQLError[]): boolean => {
    return e?.some(
        (error: GraphQLError) =>
            error.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' ||
            error.extensions?.code === 'INTERNAL_SERVER_ERROR'
    );
};

export const getServerSideProps: GetServerSideProps = async ({
    req,
    query
}) => {
    if (typeof window === 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const newrelic = require('newrelic');
        newrelic.setTransactionName('/house-prices/');
    }

    const searchQuery = query.q;

    if (!searchQuery) {
        return {
            props: {}
        };
    }

    let redirect: Redirect | undefined;

    const apolloClient = initializeApollo(null, req.headers);
    let response;
    let error = null;

    try {
        response = await apolloClient.query<
            GeoSearchQuery,
            GeoSearchQueryVariables
        >({
            query: HOUSE_PRICES_LANDING_QUERY,
            variables: {
                geoString: searchQuery.toString()
            }
        });

        if (
            response?.data?.propertiesSearch?.__typename ===
            'PropertiesSearchConnection'
        ) {
            const resultsGeoIdentifier =
                response.data.propertiesSearch?.metaData?.input?.geoIdentifier;

            redirect = {
                destination: `/house-prices/${resultsGeoIdentifier}/?q=${searchQuery}&search_source=house-prices`,
                permanent: false
            };
        }
    } catch (e) {
        const isNetworkError = !!e?.networkError;

        if (isCrash(e?.graphQLErrors) || isNetworkError) {
            console.error('Error: ', e);
        }

        error = e.graphQLErrors[0];

        if (
            error.extensions?.response?.body?.error ===
            `geo_query_string doesn't have any matching areas`
        ) {
            redirect = {
                destination: `/no-results/?q=${searchQuery}&section=house-prices`,
                permanent: false
            };
        }
    }

    return {
        redirect,
        props: {
            data: {
                ...response?.data
            },
            error: error || null,
            initialApolloState: apolloClient.cache.extract()
        }
    };
};

export default HousePricesLandingPage;
